import { USER_EVENTS } from '@shared/core/user-events'
import classNames from 'classnames'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import { CalendarsTab } from 'src/modules/calendar/containers'
import { FormattedText, PageWrapper, Typography } from 'src/modules/core/components'
import { BillingTab, SubscriptionPlanTab } from 'src/modules/payment/containers'
import { useHasUserEvent } from 'src/modules/user-events/use-has-user-event'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { SettingsMyDetailsTab } from '..'
import { useIsBookingEnabled } from '../../hooks/use-is-booking-enabled'
import { SettingsBookingPageTab } from '../settings-booking-page-tab'
import { SettingsTemplatesTab } from '../settings-templates-tab'
import { resolveSettingsTab, SettingsTabName } from './settings-info-wrapper.domain'
import classes from './settings-info-wrapper.module.scss'

type SettingsTab = {
  name: SettingsTabName
  title: string
  component: FC
}

export const SettingsInfoWrapper: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const profile = useProfile()
  const { isBookingEnabled, isFlagLoading } = useIsBookingEnabled()
  const { data: hasDismissedEvent, isLoading: isDismissedEventLoading } = useHasUserEvent(
    USER_EVENTS.APPOINTMENT_BOOKING_DISMISSED,
  )
  const { data: bookingOnboarding, isLoading: isBookingOnboardingLoading } = useBookingOnboarding()
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [prevTabIndex, setPrevTabIndex] = useState(0)

  const settingsTabs = useMemo<SettingsTab[]>(() => {
    const tabs: SettingsTab[] = [
      {
        name: 'templates',
        title: 'Templates',
        component: SettingsTemplatesTab,
      },
      {
        name: 'plan',
        title: 'Plan',
        component: SubscriptionPlanTab,
      },
      {
        name: 'billing',
        title: 'Billing',
        component: BillingTab,
      },
      {
        name: 'calendars',
        title: 'Calendars',
        component: CalendarsTab,
      },
    ]

    if (profile.data?.usage_mode === 'customer') {
      tabs.push({
        name: 'other-settings',
        title: 'Other Settings',
        component: SettingsMyDetailsTab,
      })
    } else {
      tabs.unshift({
        name: 'default-template',
        title: 'Default Template',
        component: SettingsMyDetailsTab,
      })
    }

    if (isBookingEnabled) {
      tabs.push({
        name: 'booking-page',
        title: 'Booking Page',
        component: SettingsBookingPageTab,
      })
    }

    // Accounts with the billing UI hidden have no plan to change and no
    // invoices to read. The tabs are filtered out of the visible list only;
    // `resolveSettingsTab` still resolves legacy numbers against the full
    // layout, so an old `?tab=2` link keeps landing where it lands today.
    if (profile.data?.hide_billing_ui) {
      return tabs.filter(({ name }) => name !== 'plan' && name !== 'billing')
    }

    return tabs
  }, [profile.data?.usage_mode, profile.data?.hide_billing_ui, isBookingEnabled])

  const visibleTabNames = useMemo(() => settingsTabs.map(({ name }) => name), [settingsTabs])

  useEffect(() => {
    // `usage_mode` decides how a legacy `?tab=N` resolves and the feature flag
    // decides whether the Booking Page tab exists, so resolving before both
    // settle would misroute the link.
    if (profile.isLoading || isFlagLoading || visibleTabNames.length === 0) {
      return
    }

    const { selected, shouldReplaceUrl } = resolveSettingsTab(
      searchParams.get('tab'),
      profile.data?.usage_mode,
      visibleTabNames,
    )

    const selectedIndex = visibleTabNames.indexOf(selected)

    if (selectedIndex >= 0 && selectedIndex !== tabIndex) {
      setTabIndex((prev) => {
        setPrevTabIndex(prev)

        return selectedIndex
      })
    }

    if (shouldReplaceUrl) {
      navigate(`${location.pathname}?tab=${selected}`, { replace: true })
    }
  }, [
    isFlagLoading,
    location.pathname,
    navigate,
    profile.data?.usage_mode,
    profile.isLoading,
    searchParams,
    tabIndex,
    visibleTabNames,
  ])

  const handleTabChange = useCallback(
    (index: number) => () => {
      const name = settingsTabs[index]?.name

      setTabIndex((prev) => {
        setPrevTabIndex(prev)

        return index
      })

      if (name) {
        navigate(`${location.pathname}?tab=${name}`)
      }
    },
    [location.pathname, navigate, settingsTabs],
  )

  return (
    <PageWrapper title={<FormattedText id="settings.header" />} className={classes.wrapper} authInfo>
      <div className={classes.root}>
        <div className={classes.tabs}>
          {settingsTabs.map(({ name, title }, i) => (
            <Typography
              key={name}
              variant="body"
              className={classNames(classes.tab, {
                [classes.selected]: tabIndex === i,
              })}
              onClick={handleTabChange(i)}
            >
              {title}
              {name === 'booking-page' &&
                !isDismissedEventLoading &&
                !isBookingOnboardingLoading &&
                !hasDismissedEvent &&
                !bookingOnboarding?.onboarding_completed && <span className={classes.newRibbon}>New</span>}
            </Typography>
          ))}
        </div>
        {settingsTabs.map(({ component: Component }, index) => (
          <CSSTransition
            key={index}
            in={tabIndex === index}
            timeout={500}
            classNames={{
              enter: prevTabIndex < index ? classes.rightEnter : classes.leftEnter,
              enterActive: prevTabIndex < index ? classes.rightEnterActive : classes.leftEnterActive,
              exit: tabIndex < index ? classes.rightExit : classes.leftExit,
              exitActive: tabIndex < index ? classes.rightExitActive : classes.leftExitActive,
            }}
            unmountOnExit
          >
            <Component />
          </CSSTransition>
        ))}
      </div>
    </PageWrapper>
  )
}
